import React, { useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import Lenis from "lenis";
import { breakpoints } from "../styles/responsive";
import Header from "../components/Header";
import Services from "../components/Services";
import Portfolio from "../components/Portfolio";
import Clients from "../components/Clients";
import TechStack from "../components/TechStack";
import Contact from "../components/Contact";
import Footer from "../components/Footer";

const MainContainer = styled.main`
  height: 100vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SectionWrapper = styled.section`
  min-height: 100vh;
  width: 100%;
  position: relative;
`;

const FooterWrapper = styled.section`
  width: 100%;
  position: relative;
`;

const sectionIds = [
  "header",
  "services",
  "portfolio",
  "clients",
  "techstack",
  "contact",
];

const isMobileViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia(`(max-width: ${breakpoints.tablet - 1}px)`).matches;

const Home: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityTime = useRef(0);
  const isSnapping = useRef(false);

  const findNearestSection = useCallback((): HTMLElement | null => {
    if (isMobileViewport()) return null;
    if (!containerRef.current) return null;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const viewportHeight = container.clientHeight;
    const maxScroll = container.scrollHeight - viewportHeight;

    if (maxScroll - scrollTop < viewportHeight * 0.3) {
      return null;
    }

    let nearestElement: HTMLElement | null = null;
    let minDistance = Infinity;

    for (const id of sectionIds) {
      const element = document.getElementById(id);
      if (!element) continue;

      const elementTop = element.offsetTop;
      const distance = Math.abs(scrollTop - elementTop);

      if (distance < minDistance) {
        minDistance = distance;
        nearestElement = element;
      }
    }

    const threshold = viewportHeight * 0.05;
    if (minDistance < threshold) {
      return null;
    }

    return nearestElement;
  }, []);

  const snapToNearest = useCallback(() => {
    if (isMobileViewport()) return;
    if (!lenisRef.current || isSnapping.current) return;

    const nearest = findNearestSection();
    if (!nearest) return;

    isSnapping.current = true;

    lenisRef.current.scrollTo(nearest, {
      duration: 1.0,
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
      onComplete: () => {
        isSnapping.current = false;
      },
    });
  }, [findNearestSection]);

  const scheduleSnap = useCallback(() => {
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
    }

    if (isMobileViewport()) return;

    snapTimeoutRef.current = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTime.current;
      if (timeSinceLastActivity >= 300) {
        snapToNearest();
      }
    }, 150);
  }, [snapToNearest]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const lenis = new Lenis({
      wrapper: container,
      content: container,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      orientation: "vertical",
      gestureOrientation: "vertical",
    });

    lenisRef.current = lenis;

    const raf = (time: number) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    const handleUserActivity = () => {
      lastActivityTime.current = Date.now();

      if (isSnapping.current) {
        isSnapping.current = false;
      }

      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
      }
    };

    const handleScrollEnd = () => {
      scheduleSnap();
    };

    container.addEventListener("wheel", handleUserActivity, { passive: true });
    container.addEventListener("touchstart", handleUserActivity, {
      passive: true,
    });
    container.addEventListener("touchmove", handleUserActivity, {
      passive: true,
    });
    container.addEventListener("pointerdown", handleUserActivity, {
      passive: true,
    });

    container.addEventListener("scrollend", handleScrollEnd, { passive: true });

    let scrollEndFallback: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (scrollEndFallback) clearTimeout(scrollEndFallback);
      scrollEndFallback = setTimeout(() => {
        handleScrollEnd();
      }, 150);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      if (scrollEndFallback) clearTimeout(scrollEndFallback);
      container.removeEventListener("wheel", handleUserActivity);
      container.removeEventListener("touchstart", handleUserActivity);
      container.removeEventListener("touchmove", handleUserActivity);
      container.removeEventListener("pointerdown", handleUserActivity);
      container.removeEventListener("scrollend", handleScrollEnd);
      container.removeEventListener("scroll", handleScroll);
      lenis.destroy();
    };
  }, [scheduleSnap]);

  return (
    <MainContainer ref={containerRef}>
      <SectionWrapper id="header">
        <Header />
      </SectionWrapper>
      <SectionWrapper id="services">
        <Services />
      </SectionWrapper>
      <SectionWrapper id="portfolio">
        <Portfolio />
      </SectionWrapper>
      <SectionWrapper id="clients">
        <Clients />
      </SectionWrapper>
      <SectionWrapper id="techstack">
        <TechStack />
      </SectionWrapper>
      <SectionWrapper id="contact">
        <Contact />
      </SectionWrapper>
      <FooterWrapper id="footer">
        <Footer />
      </FooterWrapper>
    </MainContainer>
  );
};

export default Home;
